import React, { useEffect, useState } from "react";
import LeftSidebar from "./components/LeftSidebar";
import RightSidebar from "./components/RightSidebar";
import MainBody from "./components/MainBody";
import API from "./config/API";
import "./App.css";
import { v4 as uuidv4 } from "uuid";
import SignIn from "./components/SignIn";
import { useAuth } from "./AuthContext";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function AppContent() {
  const { userId, login, logout } = useAuth();

  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedReportFiles, setSelectedReportFiles] = useState(null);
  const [selectedReportMessages, setSelectedReportMessages] = useState([]);
  const [pendingReport, setPendingReport] = useState(false);
  const [isLoadingRightSidebar, setIsLoadingRightSidebar] = useState(false);
  const [isLoadingLeftSidebar, setIsLoadingLeftSidebar] = useState(false);
  const [isLoadingMain, setIsLoadingMain] = useState(false);
  const [editingReportId, setEditingReportId] = useState(null);
  const [titleInput, setTitleInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLeftCollapsed, setIsLeftCollapsed] = useState(false);
  const [isRightCollapsed, setIsRightCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [justSwitchedReport, setJustSwitchedReport] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState([]);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const [input, setInput] = useState("");

  const showSnackbar = (message, severity) => {
    console.log("showSnackbar called:", message, severity);
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const refreshReportsList = async () => {
    if (!userId) return;
    setIsLoadingLeftSidebar(true);
    try {
      const res = await API.get(`/reports/${userId}`);
      const reports = res.data.reports;
      console.log("reports", reports);
      setReports(reports);
      if (reports.length > 0 && !selectedReport) {
        setSelectedReport(reports[0]);
      }
    } catch (err) {
      console.error("Failed to refresh reports:", err);
      showSnackbar("Failed to load reports", "error");
    } finally {
      setIsLoadingLeftSidebar(false);
    }
  };

  const fetchTemplates = async () => {
    if (!userId) return;
    try {
      const res = await API.get(`/templates/user/${userId}`);
      if (res.data.status === "ok") {
        setTemplates(res.data.templates);
      } else {
        showSnackbar("Failed to load templates", "error");
      }
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      showSnackbar("Failed to fetch templates", "error");
    }
  };

  const refreshReportFilesList = async () => {
    if (!userId) return;
    setIsLoadingRightSidebar(true);
    try {
      const filesRes = await API.get(
        `/reports/${selectedReport.report_id}/files`,
      );
      setSelectedReportFiles(filesRes.data.files);
    } catch (err) {
      console.error("Failed to refresh files:", err);
      showSnackbar("Failed to load files", "error");
    } finally {
      setIsLoadingRightSidebar(false);
    }
  };

  useEffect(() => {
    if (!userId) return;
    const fetchReports = async () => {
      try {
        setIsLoadingLeftSidebar(true);
        const res = await API.get(`/reports/${userId}`);
        const reports = res.data.reports;
        setReports(reports);
        if (reports.length > 0) {
          setSelectedReport(reports[0]);
        } else {
          const newConvId = uuidv4();
          const tempReport = {
            report_id: newConvId,
            title: "",
            pending: true,
          };
          setSelectedReport(tempReport);
          setSelectedReportMessages([]);
          setPendingReport(true);
        }
      } catch (err) {
        console.error("Failed to fetch Reports:", err);
        showSnackbar("Failed to fetch Reports", "error");
      } finally {
        setIsLoadingLeftSidebar(false);
      }
    };

    fetchReports();
    fetchTemplates();
  }, [userId]);

  useEffect(() => {
    if (!selectedReport?.report_id || pendingReport) return;
    const fetchData = async () => {
      try {
        setIsLoadingRightSidebar(true);
        setIsLoadingMain(true);
        const [messagesRes, filesRes] = await Promise.all([
          API.get(`/reports/${selectedReport.report_id}/messages`),
          API.get(`/reports/${selectedReport.report_id}/files`),
        ]);
        setSelectedReportMessages(messagesRes.data.messages);
        setSelectedReportFiles(filesRes.data.files);
      } catch (err) {
        console.error("Failed to fetch Report data:", err);
        showSnackbar("Error loading Report content", "error");
      } finally {
        setIsLoadingRightSidebar(false);
        setIsLoadingMain(false);
      }
    };

    fetchData();
  }, [selectedReport]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "ArrowLeft") {
        e.preventDefault();
        setIsLeftCollapsed((prev) => !prev);
      } else if (e.ctrlKey && e.key === "ArrowRight") {
        e.preventDefault();
        setIsRightCollapsed((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const buttonStyle = {
    backgroundColor: localStorage.getItem("userMainColor") || "#26a69a",
    borderRadius: "8px", // fallback to className style
  };
  const spinnerStyle = {
    borderColor: localStorage.getItem("userMainColor") || "#26a69a",
  };
  const snackbarColor = localStorage.getItem("userMainColor") || "#26a69a";
  const logoutColor = localStorage.getItem("userMainColor") || "#26a69a";
  const genericColor = localStorage.getItem("userMainColor") || "#26a69a";

  const handleSignIn = async ({ username, password, otp }) => {
    try {
      const res = await API.post(`/signin`, { username, password, otp });

      console.log(res.data.message);
      console.log("resp", res.data.data);

      if (res.data.message == "ok") {
        const {
          token,
          id,
          type,
          username: returnedUsername,
          logo,
          main_color,
        } = res.data.data;
        localStorage.setItem(
          "token",
          JSON.stringify({
            token,
            username: returnedUsername,
            id,
            type,
            name: returnedUsername,
          }),
        );
        localStorage.setItem("userLogo", logo || "");
        localStorage.setItem("userMainColor", main_color || "");
        login({ username: returnedUsername, id, type, name: returnedUsername });
        showSnackbar("Signed in successfully", "success");
      } else if (res.data.message == "error") {
        showSnackbar(res.data.data, "error");
      }
    } catch (res) {
      const msg = res.data?.data || "Login erro2r";
      console.log(msg);
      console.error("Login failed:", msg);

      showSnackbar("Signed in successfully", "success");
    }
  };

  return (
    <>
      {!userId ? (
        <SignIn onSubmit={handleSignIn} showSnackbar={showSnackbar} />
      ) : (
        <div
          className={`app-container ${darkMode ? "dark-mode" : ""}`}
          style={{ display: "flex", height: "100vh" }}
        >
          <LeftSidebar
            {...{
              selectedReport,
              setSelectedReport,
              reports,
              selectedReportMessages,
              setSelectedReportMessages,
              selectedReportFiles,
              setSelectedReportFiles,
              pendingReport,
              setPendingReport,
              isLoadingLeftSidebar,
              setIsLoadingLeftSidebar,
              refreshReportsList,
              editingReportId,
              setEditingReportId,
              titleInput,
              setTitleInput,
              setReports,
              userId,
              searchQuery,
              setSearchQuery,
              isCollapsed: isLeftCollapsed,
              toggleCollapse: () => setIsLeftCollapsed((prev) => !prev),
              setJustSwitchedReport,
              showSnackbar,
              isLoading,
              setIsLoading,
              buttonStyle,
              spinnerStyle,
              genericColor,
              input,
              setInput,
            }}
          />
          <MainBody
            {...{
              selectedReport,
              setSelectedReport,
              selectedReportMessages,
              setSelectedReportMessages,
              pendingReport,
              setPendingReport,
              refreshReportsList,
              userId,
              isLoadingMain,
              setIsLoadingMain,
              isLeftCollapsed,
              isRightCollapsed,
              justSwitchedReport,
              setJustSwitchedReport,
              showSnackbar,
              refreshReportFilesList,
              isLoading,
              setIsLoading,
              buttonStyle,
              spinnerStyle,
              templates, // 👈 new prop
              setTemplates,
              fetchTemplates,
              input,
              setInput,
            }}
          />
          <RightSidebar
            {...{
              selectedReport,
              selectedReportFiles,
              setSelectedReportFiles,
              isLoadingRightSidebar,
              setIsLoadingRightSidebar,
              isCollapsed: isRightCollapsed,
              toggleCollapse: () => setIsRightCollapsed((prev) => !prev),
              darkMode,
              setDarkMode,
              logout,
              justSwitchedReport,
              setJustSwitchedReport,
              refreshReportsList,
              showSnackbar,
              refreshReportFilesList,
              isLoading,
              setIsLoading,
              buttonStyle,
              spinnerStyle,
              logoutColor,
              genericColor,
            }}
          />
        </div>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{
            backgroundColor:
              snackbarSeverity === "error" ? "#d32f2f" : snackbarColor,
            color: "#fff",
          }}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}
export default AppContent;
